// Berkshire Investment Group — The $500 Billion Bet on Africa
// Strategic content data. Used by StrategyHome and /strategy/* deep-dive pages.

export const strategy = {
  hero: {
    eyebrow: "Strategic Plan · 2026–2046",
    headline: "The $500 Billion Bet on Africa",
    sub: "A 20-year strategy to build the America–Africa strategic infrastructure and industrial platform.",
    body: "Berkshire Investment Group Inc. — BIG — is a permanent-capital industrial, infrastructure and investment platform connecting Africa's most important economic opportunities with American capital, technology, corporations and operating expertise.",
    primary: "Capital Partners",
    secondary: "Sovereign Partners",
    tertiary: "U.S. Corporate Partners"
  },
  headlineStats: [
    { value: "$500B", label: "Capital deployed & mobilised", sub: "Cumulative 2026 – 2046" },
    { value: "$250B", label: "Assets under control", sub: "Owned / managed / strategic" },
    { value: "$60–80B", label: "Annual revenue", sub: "By 2046" },
    { value: "30+",     label: "Countries of operation", sub: "Across the continent" },
    { value: "40+ GW",  label: "Energy developed", sub: "Generated, financed or operated" },
    { value: "100K+",   label: "Direct employment", sub: "1.5M+ wider jobs supported" },
  ],
  thesis: {
    title: "Africa's opportunity is not constrained by resources. It is constrained by fragmented execution.",
    body: [
      "Governments may possess valuable minerals but lack electricity, rail and processing. Industrial parks may have land but lack utilities and anchor tenants. Power projects may have demand but lack creditworthy payment structures. Ports may have volume but lack inland corridors.",
      "BIG integrates these fragmented elements. We use capital to secure assets, assets to produce cash flow, infrastructure to increase productivity, technology to improve operations, and sovereign partnerships to generate proprietary opportunities."
    ]
  },
  bridge: {
    title: "An institutional bridge between two complementary markets.",
    africa: {
      label: "Africa contributes",
      items: ["Critical minerals", "Energy resources", "Agricultural production", "Land", "Growing cities", "Young consumers", "Infrastructure demand", "Industrialisation opportunities", "Underutilised public assets", "Strategic geographic corridors"]
    },
    usa: {
      label: "The United States contributes",
      items: ["Institutional capital", "Capital-market expertise", "Artificial intelligence", "Advanced engineering", "Energy technology", "Digital infrastructure", "Aerospace & satellite", "Industrial equipment", "Cybersecurity", "Intellectual property", "Global corporations", "Export & development finance"]
    }
  },
  flywheel: [
    "Capital mobilisation",
    "Secure strategic rights, concessions and assets",
    "Develop and finance infrastructure",
    "Operate and improve asset performance",
    "Increase national productivity & export earnings",
    "Generate recurring cash flow",
    "Recycle capital into larger projects",
    "Secure additional sovereign opportunities",
    "Compound permanently"
  ],
  // 8 strategic platforms — summary cards on home; full detail on /strategy/platforms
  platforms: [
    {
      code: "BIG Energy",
      title: "Energy & Power Systems",
      target: "$110B",
      tagline: "Africa's preferred independent developer, investor and operator of integrated energy systems.",
      bullets: ["Utility-scale solar, wind, hydro", "LNG, gas-to-power & industrial gas", "Transmission, distribution & storage", "Captive power & industrial microgrids", "Power for mines, data centres & industrial parks"],
      returns: "12–20% levered equity"
    },
    {
      code: "BIG Critical Minerals",
      title: "Critical Minerals & Processing",
      target: "$85B",
      tagline: "Strategic, profitable positions across the minerals required for electrification, advanced manufacturing and supply-chain resilience.",
      bullets: ["Copper, cobalt, lithium, graphite, nickel, manganese, rare earths, uranium, gold", "Mine acquisition, brownfield expansion, royalties & streaming", "Processing, refining, smelting & recycling", "Reserve-backed and working-capital finance", "Bundled power + logistics + offtake rights"],
      returns: "18–25%"
    },
    {
      code: "BIG Infrastructure",
      title: "Infrastructure & Logistics",
      target: "$85B",
      tagline: "The arteries of African commerce — ports, rail, corridors and trade infrastructure.",
      bullets: ["Sea ports, container & bulk terminals", "Dry ports, rail, toll roads, border facilities", "Cold storage & warehousing", "Commodity corridors integrating mine→processor→port", "Digital customs & cargo tracking"],
      returns: "13–17%"
    },
    {
      code: "BIG Industrial Platforms",
      title: "Industrial & Manufacturing Platforms",
      target: "$55B",
      tagline: "The environments in which African manufacturing and processing take place.",
      bullets: ["Special economic zones & export-processing zones", "Industrial, technology & mineral-processing parks", "Pharmaceutical, textile & food-processing clusters", "Combined land + power + water + connectivity + logistics", "Tenant finance & equity in anchor tenants"],
      returns: "16–22%"
    },
    {
      code: "BIG Commodities",
      title: "Commodities & Food Systems",
      target: "$45B",
      tagline: "Leading African merchant and financier of strategic commodities, with food security at its core.",
      bullets: ["Copper, grain, fertiliser, coffee, cocoa, cotton, sugar, oilseeds, LNG, LPG", "Trade, pre-export, warehouse and receivables finance", "Storage, shipping, insurance & hedging", "Linked to BIG-owned infrastructure & contracted offtake", "No uncontrolled proprietary trading"],
      returns: "12–20% RoE"
    },
    {
      code: "BIG Digital",
      title: "Digital Infrastructure",
      target: "$40B",
      tagline: "The essential infrastructure behind Africa's digital economy.",
      bullets: ["Hyperscale, regional & edge data centres", "Fibre, towers, satellite connectivity, AI compute", "Cloud, cybersecurity & government cloud", "Digital identity, payments & public infrastructure", "Linked to BIG Energy for dedicated power"],
      returns: "16–24%"
    },
    {
      code: "BIG Capital",
      title: "BIG Capital",
      target: "Cross-platform",
      tagline: "The financial engine for BIG's operating platforms.",
      bullets: ["Private credit, infrastructure debt, project finance", "Trade, export, asset & structured credit", "Sovereign & sub-sovereign finance", "Securitisation & capital-markets advisory", "Special situations, distressed & asset-backed credit"],
      returns: "12–18%"
    },
    {
      code: "BIG Sovereign",
      title: "Sovereign Partnerships",
      target: "$60B",
      tagline: "Long-term strategic development and investment partner to selected African governments.",
      bullets: ["National investment-platform design & PPP structuring", "State-owned enterprise optimisation & asset recycling", "Industrial policy implementation & export development", "Strategic project preparation & capital mobilisation", "Transparent frameworks under public-procurement law"],
      returns: "Strategic"
    }
  ],
  crossCutting: [
    { name: "Berkshire AI", description: "The intelligence layer across BIG's assets — commodity intelligence, sovereign-risk monitoring, energy dispatch, predictive maintenance, credit underwriting, digital twins and government decision support." },
    { name: "Berkshire OnePass", description: "Unified identity, payments, connectivity, credit and service-access platform for African consumers, businesses and governments — built on consent, privacy and data-sovereignty standards." },
    { name: "Berkshire Industrial Development Corp.", description: "Institutional project-development and operating capability addressing Africa's principal investment constraint: the shortage of bankable, well-prepared projects." }
  ],
  // Capital architecture — for /strategy/capital
  capitalSources: [
    { source: "BIG permanent capital & sponsored equity vehicles", amount: "$75B" },
    { source: "Institutional co-investment equity",                amount: "$50B" },
    { source: "Project, acquisition & corporate debt",             amount: "$180B" },
    { source: "Sovereign, DFI, MDB & public co-investment",        amount: "$60B" },
    { source: "Trade finance, warehouse & securitisation",         amount: "$65B" },
    { source: "Strategic corporate, customer & offtake finance",   amount: "$40B" },
    { source: "Retained earnings & recycled capital",              amount: "$30B" }
  ],
  capitalUses: [
    { use: "Energy & power systems",                          amount: "$110B" },
    { use: "Critical minerals & mineral processing",          amount: "$85B" },
    { use: "Transport, ports & logistics infrastructure",     amount: "$85B" },
    { use: "Industrial platforms & manufacturing",            amount: "$55B" },
    { use: "Commodities, food systems & agricultural trade",  amount: "$45B" },
    { use: "Digital infrastructure",                          amount: "$40B" },
    { use: "Sovereign-linked infrastructure",                 amount: "$60B" },
    { use: "AI, OnePass & cross-platform systems",            amount: "$20B" }
  ],
  capitalProgression: [
    { stage: "Sponsor capital",                                  size: "$50–100M" },
    { stage: "Founding permanent-capital round",                 size: "$500M" },
    { stage: "Institutional growth capital",                     size: "$2–5B" },
    { stage: "Strategic corporate & sovereign capital",          size: "$5–10B" },
    { stage: "Public-market or large private recapitalisation",  size: "$10–20B" },
    { stage: "Retained earnings & asset recycling",              size: "Compounding" }
  ],
  // 20-year roadmap — for /strategy/roadmap
  phases: [
    {
      label: "Phase I",
      title: "Foundation",
      years: "2026 – 2028",
      objectives: [
        "Establish institutional governance & resolve group structure",
        "Capitalise the project-development platform",
        "Enter five anchor countries",
        "Sign three sovereign or strategic frameworks",
        "Close initial energy, credit, digital and logistics transactions"
      ],
      targets: [
        { k: "Cumulative deployment", v: "$5B" },
        { k: "Countries",             v: "5" },
        { k: "Material projects",     v: "10+" },
        { k: "Annual revenue",        v: "$500M" },
        { k: "Annual EBITDA",         v: "$100M" },
        { k: "Attributable NAV",      v: "$750M – $1B" }
      ]
    },
    {
      label: "Phase II",
      title: "Regional Scale",
      years: "2029 – 2032",
      objectives: [
        "Establish regional platform companies",
        "Scale private credit and trade finance",
        "Begin large corridor and industrial projects",
        "Acquire operating cash-flow assets",
        "Expand institutional investor base"
      ],
      targets: [
        { k: "Cumulative deployment", v: "$30B" },
        { k: "Countries",             v: "12" },
        { k: "Capital under control", v: "$20B" },
        { k: "Annual revenue",        v: "$4B" },
        { k: "Annual EBITDA",         v: "$900M" },
        { k: "Attributable NAV",      v: "$5B" }
      ]
    },
    {
      label: "Phase III",
      title: "Continental Integration",
      years: "2033 – 2037",
      objectives: [
        "Connect energy, mineral & logistics platforms",
        "Build regional commodity corridors",
        "Scale digital infrastructure",
        "Launch yield and securitisation vehicles",
        "Expand to 20 markets"
      ],
      targets: [
        { k: "Cumulative deployment", v: "$125B" },
        { k: "Countries",             v: "20" },
        { k: "Capital under control", v: "$70B" },
        { k: "Annual revenue",        v: "$15B" },
        { k: "Annual EBITDA",         v: "$3.5–4.5B" },
        { k: "Attributable NAV",      v: "$18–22B" }
      ]
    },
    {
      label: "Phase IV",
      title: "Institutionalisation",
      years: "2038 – 2041",
      objectives: [
        "Deepen platform leadership",
        "Create listed or perpetual subsidiaries",
        "Expand U.S. industrial participation",
        "Develop local-currency capital markets",
        "Institutionalise succession & management"
      ],
      targets: [
        { k: "Cumulative deployment", v: "$275B" },
        { k: "Countries",             v: "25+" },
        { k: "Capital under control", v: "$145B" },
        { k: "Annual revenue",        v: "$35B" },
        { k: "Annual EBITDA",         v: "$8–10B" },
        { k: "Attributable NAV",      v: "$40–50B" }
      ]
    },
    {
      label: "Phase V",
      title: "The $500B Platform",
      years: "2042 – 2046",
      objectives: [
        "Reach continental scale",
        "Maintain investment discipline",
        "Consolidate strategic infrastructure networks",
        "Become a major allocator of global capital into Africa",
        "Produce self-sustaining reinvestment capacity"
      ],
      targets: [
        { k: "Cumulative deployment",  v: "$500B" },
        { k: "Countries",              v: "30+" },
        { k: "Capital under control",  v: "$250B" },
        { k: "Annual revenue",         v: "$60–80B" },
        { k: "Annual EBITDA",          v: "$12–18B" },
        { k: "Attributable NAV",       v: "$60–80B" }
      ]
    }
  ],
  geographies: {
    phase1: { label: "Phase I — Anchor markets (2026–2030)", countries: ["Kenya", "Tanzania", "Rwanda", "Uganda", "Zambia", "Botswana", "DRC", "Ghana"] },
    phase2: { label: "Phase II — Regional scaling (2029–2035)", countries: ["Namibia", "South Africa", "Angola", "Côte d'Ivoire", "Senegal", "Ethiopia", "Nigeria", "Mozambique"] },
    phase3: { label: "Phase III — Continental integration (2033–2040)", countries: ["Republic of the Congo", "Gabon", "Benin", "Zimbabwe", "Additional West & Central African markets", "Select North African markets"] }
  },
  audiences: {
    capital: {
      key: "capital",
      title: "Capital Partners",
      pitch: "Anchor or co-invest in the founding $500M permanent-capital round and subsequent institutional rounds. Targets institutional family offices, sovereigns, DFIs, pensions, insurers, infrastructure funds and US strategic capital.",
      cta: "Capital Partner Briefing",
      color: "#2f9e8e"
    },
    sovereign: {
      key: "sovereign",
      title: "Sovereign Partners",
      pitch: "Long-term strategic development partnerships with African governments — national investment-platform design, PPP structuring, SOE optimisation, asset recycling and industrial policy execution.",
      cta: "Sovereign Briefing",
      color: "#c19a3c"
    },
    corporate: {
      key: "corporate",
      title: "U.S. Corporate Partners",
      pitch: "Joint ventures, technology licensing, preferred-supplier and build-operate-transfer arrangements with U.S. industrial, energy, AI, defence-adjacent and engineering corporations.",
      cta: "Corporate Briefing",
      color: "#0d6efd"
    }
  },
  notice: "This document and the strategy summarised on these pages are a strategic business plan and do not constitute an offer of securities, financial advice, a commitment of capital or a guarantee of investment performance. All transactions remain subject to legal, regulatory, tax, technical, environmental, commercial and financial due diligence."
};
